import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  LinearProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Campaign as CampaignIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../config';
import GoogleMCPNavigation from '../components/GoogleMCPNavigation';

interface Lead {
  id: number;
  lead_id: string;
  campaign_id: string;
  campaign_name?: string;
  name: string;
  email: string;
  phone?: string;
  location?: string;
  source_keyword: string;
  landing_page: string;
  interest_score: number;
  status: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  device_type?: string;
  browser?: string;
  ip_address?: string;
  created_at: string;
  last_interaction?: string;
  conversion_data?: {
    page_views: number;
    time_on_site: number;
    form_submissions: number;
    downloads: number;
  };
}

interface LeadStats {
  total_leads: number;
  new_leads_today: number;
  qualified_leads: number;
  conversion_rate: number;
  avg_interest_score: number;
}

const GoogleMCPLeads: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stats, setStats] = useState<LeadStats>({
    total_leads: 0,
    new_leads_today: 0,
    qualified_leads: 0,
    conversion_rate: 0,
    avg_interest_score: 0
  });
  const [loading, setLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [scoreFilter, setScoreFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  const leadStatuses = [
    { value: 'new', label: 'Novo', color: 'info' },
    { value: 'contacted', label: 'Contatado', color: 'warning' },
    { value: 'qualified', label: 'Qualificado', color: 'success' },
    { value: 'converted', label: 'Convertido', color: 'primary' },
    { value: 'lost', label: 'Perdido', color: 'error' }
  ];

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/google-mcp/leads`);
      setLeads(response.data.data || []);
    } catch (error: any) {
      console.error('Erro ao carregar leads:', error);
      showSnackbar('Erro ao carregar leads', 'error');
      // Dados simulados para desenvolvimento
      setLeads([
        {
          id: 1,
          lead_id: 'lead_001',
          campaign_id: 'camp_001',
          campaign_name: 'Consórcio Carros - Geral',
          name: 'João Silva',
          email: 'joao.silva@email.com',
          location: 'São Paulo, SP',
          source_keyword: 'consórcio carros',
          landing_page: '/consorcio-carros',
          interest_score: 85,
          status: 'qualified',
          utm_source: 'google',
          utm_medium: 'cpc',
          utm_campaign: 'consorcio-carros-geral',
          device_type: 'mobile',
          browser: 'Chrome',
          ip_address: '192.168.1.1',
          created_at: '2024-01-15T10:30:00Z',
          last_interaction: '2024-01-15T14:20:00Z',
          conversion_data: {
            page_views: 5,
            time_on_site: 420,
            form_submissions: 2,
            downloads: 1
          }
        },
        {
          id: 2,
          lead_id: 'lead_002',
          campaign_id: 'camp_002',
          campaign_name: 'Consórcio Imóveis - Premium',
          name: 'Maria Santos',
          email: 'maria.santos@email.com',
          phone: '(21) 88888-8888',
          location: 'Rio de Janeiro, RJ',
          source_keyword: 'consórcio imóveis',
          landing_page: '/consorcio-imoveis',
          interest_score: 92,
          status: 'new',
          utm_source: 'google',
          utm_medium: 'cpc',
          utm_campaign: 'consorcio-imoveis-premium',
          device_type: 'desktop',
          browser: 'Firefox',
          ip_address: '192.168.1.2',
          created_at: '2024-01-16T09:15:00Z',
          last_interaction: '2024-01-16T09:15:00Z',
          conversion_data: {
            page_views: 8,
            time_on_site: 680,
            form_submissions: 1,
            downloads: 2
          }
        },
        {
          id: 3,
          lead_id: 'lead_003',
          campaign_id: 'camp_001',
          campaign_name: 'Consórcio Carros - Geral',
          name: 'Pedro Oliveira',
          email: 'pedro.oliveira@email.com',
          phone: '(31) 77777-7777',
          location: 'Belo Horizonte, MG',
          source_keyword: 'financiamento carro',
          landing_page: '/consorcio-carros',
          interest_score: 67,
          status: 'contacted',
          utm_source: 'google',
          utm_medium: 'cpc',
          utm_campaign: 'consorcio-carros-geral',
          device_type: 'mobile',
          browser: 'Safari',
          ip_address: '192.168.1.3',
          created_at: '2024-01-14T16:45:00Z',
          last_interaction: '2024-01-15T11:30:00Z',
          conversion_data: {
            page_views: 3,
            time_on_site: 180,
            form_submissions: 1,
            downloads: 0
          }
        }
      ]);
    }
    setLoading(false);
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/google-mcp/leads/stats`);
      setStats(prev => response.data.data || prev);
    } catch (error: any) {
      console.error('Erro ao carregar estatísticas:', error);
      // Dados simulados para desenvolvimento
      setStats({
        total_leads: 156,
        new_leads_today: 12,
        qualified_leads: 89,
        conversion_rate: 23.5,
        avg_interest_score: 78.2
      });
    }
  }, []);

  useEffect(() => {
    fetchLeads();
    fetchStats();
  }, [fetchLeads, fetchStats]);

  const handleViewLead = (lead: Lead) => {
    setSelectedLead(lead);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedLead(null);
  };

  const updateLeadStatus = async (leadId: number, newStatus: string) => {
    try {
      await axios.patch(`${API_URL}/api/google-mcp/leads/${leadId}/status`, {
        status: newStatus
      });
      showSnackbar('Status do lead atualizado com sucesso!', 'success');
      fetchLeads();
      fetchStats();
    } catch (error: any) {
      showSnackbar('Erro ao atualizar status do lead', 'error');
    }
  };

  const exportLeads = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/google-mcp/leads/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      showSnackbar('Leads exportados com sucesso!', 'success');
    } catch (error: any) {
      showSnackbar('Erro ao exportar leads', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusChip = (status: string) => {
    const statusConfig = leadStatuses.find(s => s.value === status) || leadStatuses[0];
    return (
      <Chip 
        label={statusConfig.label} 
        color={statusConfig.color as any} 
        size="small" 
      />
    );
  };

  const getInterestScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const renderStars = (score: number) => {
    const stars = Math.round(score / 20); // Converte para escala de 1-5
    return (
      <Box sx={{ display: 'flex' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          star <= stars ? 
            <StarIcon key={star} sx={{ color: '#ffc107', fontSize: 16 }} /> : 
            <StarBorderIcon key={star} sx={{ color: '#ffc107', fontSize: 16 }} />
        ))}
      </Box>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.source_keyword.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    
    const matchesScore = scoreFilter === 'all' || 
      (scoreFilter === 'high' && lead.interest_score >= 80) ||
      (scoreFilter === 'medium' && lead.interest_score >= 60 && lead.interest_score < 80) ||
      (scoreFilter === 'low' && lead.interest_score < 60);
    
    const matchesDate = dateFilter === 'all' || 
      (dateFilter === 'today' && new Date(lead.created_at).toDateString() === new Date().toDateString()) ||
      (dateFilter === 'week' && new Date(lead.created_at) >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) ||
      (dateFilter === 'month' && new Date(lead.created_at) >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
    
    return matchesSearch && matchesStatus && matchesScore && matchesDate;
  });

  return (
    <Container maxWidth="lg">
      {/* Navegação */}
      <GoogleMCPNavigation currentPage="leads" />
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Leads Google MCP
          </Typography>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportLeads}
          >
            Exportar Leads
          </Button>
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          Gerencie os leads capturados pelas campanhas do Google MCP
        </Typography>
      </Box>

      {/* Estatísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats.total_leads}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total de Leads
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Badge badgeContent={stats.new_leads_today} color="error">
                  <ScheduleIcon color="info" sx={{ mr: 2 }} />
                </Badge>
                <Box sx={{ ml: 1 }}>
                  <Typography variant="h4">{stats.new_leads_today}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Novos Hoje
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssignmentIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats.qualified_leads}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Qualificados
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats.conversion_rate}%</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Taxa Conversão
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StarIcon color="secondary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats.avg_interest_score.toFixed(1)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Score Médio
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Buscar leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                {leadStatuses.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Score</InputLabel>
              <Select
                value={scoreFilter}
                label="Score"
                onChange={(e) => setScoreFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="high">Alto (80+)</MenuItem>
                <MenuItem value="medium">Médio (60-79)</MenuItem>
                <MenuItem value="low">Baixo (&lt;60)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Período</InputLabel>
              <Select
                value={dateFilter}
                label="Período"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="today">Hoje</MenuItem>
                <MenuItem value="week">Última Semana</MenuItem>
                <MenuItem value="month">Último Mês</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              {filteredLeads.length} lead(s) encontrado(s)
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela de Leads */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Lead</TableCell>
              <TableCell>Campanha</TableCell>
              <TableCell>Palavra-chave</TableCell>
              <TableCell>Score de Interesse</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data de Criação</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && leads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredLeads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Nenhum lead encontrado
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredLeads.map((lead) => (
                <TableRow key={lead.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {lead.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {lead.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {lead.email}
                        </Typography>
                        {lead.phone && (
                          <Typography variant="body2" color="text.secondary">
                            {lead.phone}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {lead.campaign_name || lead.campaign_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={lead.source_keyword}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {lead.interest_score}
                      </Typography>
                      {renderStars(lead.interest_score)}
                      <LinearProgress
                        variant="determinate"
                        value={lead.interest_score}
                        color={getInterestScoreColor(lead.interest_score) as any}
                        sx={{ width: 60, height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>{getStatusChip(lead.status)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(lead.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Ver detalhes">
                        <IconButton
                          size="small"
                          onClick={() => handleViewLead(lead)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar status">
                        <IconButton size="small">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal de Detalhes do Lead */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        {selectedLead && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {selectedLead.name.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedLead.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Lead ID: {selectedLead.lead_id}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                {/* Informações de Contato */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Informações de Contato
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar>
                          <EmailIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Email"
                        secondary={selectedLead.email}
                      />
                    </ListItem>
                    {selectedLead.phone && (
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar>
                            <PhoneIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary="Telefone"
                          secondary={selectedLead.phone}
                        />
                      </ListItem>
                    )}
                    {selectedLead.location && (
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar>
                            <LocationIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary="Localização"
                          secondary={selectedLead.location}
                        />
                      </ListItem>
                    )}
                  </List>
                </Grid>

                {/* Informações da Campanha */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Informações da Campanha
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar>
                          <CampaignIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Campanha"
                        secondary={selectedLead.campaign_name || selectedLead.campaign_id}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Palavra-chave"
                        secondary={selectedLead.source_keyword}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Página de Destino"
                        secondary={selectedLead.landing_page}
                      />
                    </ListItem>
                  </List>
                </Grid>

                {/* Score de Interesse */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Score de Interesse
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="h4" color={getInterestScoreColor(selectedLead.interest_score)}>
                      {selectedLead.interest_score}
                    </Typography>
                    {renderStars(selectedLead.interest_score)}
                    <LinearProgress
                      variant="determinate"
                      value={selectedLead.interest_score}
                      color={getInterestScoreColor(selectedLead.interest_score) as any}
                      sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                    />
                  </Box>
                </Grid>

                {/* Dados de Conversão */}
                {selectedLead.conversion_data && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Dados de Conversão
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h5">
                              {selectedLead.conversion_data.page_views}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Páginas Vistas
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h5">
                              {formatDuration(selectedLead.conversion_data.time_on_site)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Tempo no Site
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h5">
                              {selectedLead.conversion_data.form_submissions}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Formulários
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h5">
                              {selectedLead.conversion_data.downloads}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Downloads
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {/* Informações Técnicas */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Informações Técnicas
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>UTM Source:</strong> {selectedLead.utm_source || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>UTM Medium:</strong> {selectedLead.utm_medium || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>UTM Campaign:</strong> {selectedLead.utm_campaign || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Dispositivo:</strong> {selectedLead.device_type || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Navegador:</strong> {selectedLead.browser || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>IP:</strong> {selectedLead.ip_address || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Atualizar Status */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Atualizar Status
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {leadStatuses.map((status) => (
                      <Button
                        key={status.value}
                        variant={selectedLead.status === status.value ? 'contained' : 'outlined'}
                        color={status.color as any}
                        size="small"
                        onClick={() => updateLeadStatus(selectedLead.id, status.value)}
                      >
                        {status.label}
                      </Button>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GoogleMCPLeads;